import { useState } from 'react';

import { AccessTime, CallSplit, ExitToApp, MailOutline, Send, SwapCalls } from '@mui/icons-material';
import { FormControlLabel, FormHelperText, Radio, RadioGroup, Stack, Tooltip } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import {
    addBranchSend,
    setParentAudienceDelayInterval,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import {
    findAncestorDoesNotMeetIndex,
    findEnclosingSplitNodeId,
    getBranchRootParentIndex,
    getEffectiveSplitType,
} from 'domains/campaigns/components/CustomCampaign/utils';
import { ADD_SPLIT_NODE } from 'domains/campaigns/state/actionTypes';
import { CampaignChannels, SplitTypes } from 'domains/campaigns/types';
import FormLayout from 'domains/core/components/FormLayout';
import Select from 'domains/core/components/Select';
import TextField from 'domains/core/components/TextField';
import { useAppDispatch } from 'hooks/redux';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus } from 'models/enums';
import { RelativeTimeframeUnit } from 'models/types';

import { DisabledTooltip } from '../../../components/DisabledTooltipContent';
import {
    disabledButtonStyles,
    iconStyles,
    labelStyles,
    sharedButtonStyles,
} from '../CustomCampaignChooseNextStep/CustomCampaignChooseNextStep.styles';
import { MAX_SENDS_AMOUNT } from '../utils';

const EXIT_FLOW_ACTION = 'exit-flow';
const ADD_EMAIL_WITH_DELAY_ACTION = 'add-email-with-delay';
const ADD_SPLIT_WITH_DELAY_ACTION = 'add-split-with-delay';

const baseTimeframeValues = [
    { value: RelativeTimeframeUnit.HOUR, label: 'Hours' },
    { value: RelativeTimeframeUnit.DAY, label: 'Days' },
];

const getDelayErrorMessage = (value: string, unit: string): string => {
    const num = Number(value);
    if (num === 0) return 'The delay must be greater than 0.';
    if (!value || !unit) return '';
    const valid =
        (unit === RelativeTimeframeUnit.DAY && num < 365) ||
        (unit === RelativeTimeframeUnit.HOUR && num < 8760) ||
        (unit === RelativeTimeframeUnit.MINUTE && num < 1440);
    return valid ? '' : 'Time delays cannot be more than one year into the future.';
};

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const findDoesNotMeetSendIndex = (sends: CampaignStateTypes['sends'], parentIndex: number): number =>
    sends.findIndex(
        (send: any) => send.splitType === SplitTypes.DOES_NOT_MEET && (send.parentIndex ?? 0) === parentIndex
    );

const CustomCampaignChooseNextStepConditionalSplit = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const [selectedStep, setSelectedStep] = useState<string | null>(null);
    const [delayValue, setDelayValue] = useState('');
    const [delayUnit, setDelayUnit] = useState('');
    const dispatch = useAppDispatch();
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;
    const isEditingPausedCampaign = status === CampaignStatus.PAUSED;
    const { timeDelayMinutesUnit: hasTimeDelayMinutesUnitFlag } = useFlags();
    const timeframeValues = hasTimeDelayMinutesUnitFlag
        ? [{ value: RelativeTimeframeUnit.MINUTE, label: 'Minutes' }, ...baseTimeframeValues]
        : baseTimeframeValues;

    const activeSend = campaignState.sends[activeSendIndex] as any;
    const isBranchSend =
        activeSend?.splitType === SplitTypes.MEETS || activeSend?.splitType === SplitTypes.DOES_NOT_MEET;
    const maxSendsReached = campaignState.sends.length >= MAX_SENDS_AMOUNT;

    const effectiveSplitType = getEffectiveSplitType(activeSend, campaignState.sends);
    const isMeetsCriteriaBranch = effectiveSplitType === SplitTypes.MEETS;
    const branchRootParentIndex = getBranchRootParentIndex(activeSend, campaignState.sends);

    const resolveDoesNotMeetIndex = (): number => {
        const enclosingSplitNodeId = findEnclosingSplitNodeId(activeSend, campaignState.sends);
        if (enclosingSplitNodeId != null) {
            const nestedDnmIndex = campaignState.sends.findIndex(
                (s: any) => s.splitNodeId === enclosingSplitNodeId && s.splitType === SplitTypes.DOES_NOT_MEET
            );
            if (nestedDnmIndex !== -1) return nestedDnmIndex;
        }
        return findDoesNotMeetSendIndex(campaignState.sends, branchRootParentIndex);
    };

    // true when "time delay" has been picked and we are showing the delay config sub-form
    const isConfiguringDelay = selectedStep === CustomCampaignSteps.SET_TIME_DELAY;
    const delayError = isConfiguringDelay ? getDelayErrorMessage(delayValue, delayUnit) : '';
    const delayReady = isConfiguringDelay && !!delayValue && !!delayUnit && !delayError;

    const handleMainStepClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const step = (event.target as HTMLInputElement).value;
        setSelectedStep(step);

        if (step === EXIT_FLOW_ACTION) {
            if (isMeetsCriteriaBranch) {
                const doesNotMeetIndex = resolveDoesNotMeetIndex();
                if (doesNotMeetIndex !== -1) {
                    setActiveSendIndex(doesNotMeetIndex);
                    setActiveStep(CustomCampaignSteps.SELECT_PATH_ACTION);
                }
            } else {
                const ancestorDnmIndex = findAncestorDoesNotMeetIndex(activeSend, campaignState.sends);
                if (ancestorDnmIndex !== -1) {
                    setActiveSendIndex(ancestorDnmIndex);
                    setActiveStep(CustomCampaignSteps.SELECT_PATH_ACTION);
                } else {
                    setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
                }
            }
        }

        // SET_TIME_DELAY: just show the inline delay form — no send created yet.
        // Conditional split is only reachable via the delay sub-form (ADD_SPLIT_WITH_DELAY_ACTION)
        // so a time delay must be confirmed before a nested split can be added.
    };

    const handleDelaySubAction = (action: string) => {
        if (!delayReady) return;
        const delay = `${delayValue} ${delayUnit}`;
        const branchSplitType = isMeetsCriteriaBranch ? SplitTypes.MEETS : SplitTypes.DOES_NOT_MEET;

        if (action === ADD_EMAIL_WITH_DELAY_ACTION) {
            // Index where the new chained send will land after addBranchSend inserts it.
            // For MEETS: inserted before the DNM send (at the old DNM index).
            // For DNM: appended at the end.
            const newSendIndex = isMeetsCriteriaBranch ? resolveDoesNotMeetIndex() : campaignState.sends.length;

            // ADD_EMAIL_WITH_DELAY_ACTION is email-only by design (conditional split → time delay → email content).
            dispatchCampaignAction(addBranchSend(activeSendIndex, branchSplitType, CampaignChannels.EMAIL));
            dispatchCampaignAction(setParentAudienceDelayInterval(delay, newSendIndex));

            setActiveSendIndex(newSendIndex);
            setActiveStep(CustomCampaignSteps.SELECT_CONTENT);
        }

        if (action === ADD_SPLIT_WITH_DELAY_ACTION) {
            const existingSplitNodeIds = new Set(
                (campaignState.sends as any[]).map((s) => s.splitNodeId).filter(Boolean)
            );
            const splitNodeId = `split_${existingSplitNodeIds.size}`;
            const addSplitNodeAction = {
                type: ADD_SPLIT_NODE,
                payload: { splitNodeId, parentSendIndex: activeSendIndex, parentAudienceDelayInterval: delay },
            };
            dispatchCampaignAction(addSplitNodeAction);
            dispatch(addSplitNodeAction);
            setActiveSendIndex(campaignState.sends.length);
            setActiveStep('set-split-criteria' as any);
        }
    };

    const isMixedChannel = !!campaignState.isMixedChannel;
    const branchLabel = isMeetsCriteriaBranch ? 'meets' : 'does not meet';
    // Inner quotes on 'does not meet criteria' break a gherkin {string} param in qa-outreach
    // (you can't embed " inside ".." cleanly) — using a plain label so the test can target it
    // with the existing "Click on X Text" step. The meaning is unchanged: this transitions to
    // configuring the does-not-meet branch's path action.
    const exitLabel = isMeetsCriteriaBranch ? 'proceed to does not meet criteria' : 'done with conditional split';

    const mainSteps = [
        {
            disabled: maxSendsReached || isEditingPausedCampaign,
            disabledMessage: isEditingPausedCampaign ? (
                <DisabledTooltip />
            ) : (
                "You've reached the maximum number of sends."
            ),
            icon: <AccessTime sx={iconStyles} />,
            label: 'time delay',
            value: CustomCampaignSteps.SET_TIME_DELAY,
        },
        {
            disabled: false,
            disabledMessage: '',
            icon: <ExitToApp sx={iconStyles} />,
            label: exitLabel,
            value: EXIT_FLOW_ACTION,
        },
    ];

    const subActions = [
        {
            disabled: !delayReady || maxSendsReached,
            icon: isMixedChannel ? <Send sx={iconStyles} /> : <MailOutline sx={iconStyles} />,
            label: isMixedChannel ? 'add send' : 'add email',
            value: ADD_EMAIL_WITH_DELAY_ACTION,
        },
        ...(isBranchSend
            ? [
                  {
                      disabled: !delayReady || maxSendsReached,
                      icon: <CallSplit sx={iconStyles} />,
                      label: 'add conditional split',
                      value: ADD_SPLIT_WITH_DELAY_ACTION,
                  },
              ]
            : []),
    ];

    const sendOrEmail = isMixedChannel ? 'a send' : 'an email';
    const guidingText = `You've completed ${sendOrEmail} for the "${branchLabel} the criteria" path. Choose to add a time-delayed next step, or proceed.`;

    if (isConfiguringDelay) {
        return (
            <FormLayout
                guidingText="Set the delay, then choose what comes after it."
                header="time delay"
                stepName="time delay"
                icon={<AccessTime />}
            >
                <Stack spacing={3} width="100%">
                    <Stack spacing={2} direction="row">
                        <span style={{ width: '100%' }}>
                            <TextField
                                label="Delay"
                                type="number"
                                value={delayValue}
                                onChange={(e: any) => {
                                    const v = e.target.value;
                                    if (!v.includes('.') && !v.includes('-') && Number(v) < 10000) setDelayValue(v);
                                }}
                                fullWidth
                            />
                        </span>
                        <span style={{ width: '100%' }}>
                            <Select
                                label="Timeframe"
                                values={timeframeValues}
                                onChange={(e: any) => setDelayUnit(e.target.value)}
                                value={delayUnit}
                            />
                        </span>
                    </Stack>
                    {delayError && (
                        <FormHelperText error sx={{ alignSelf: 'flex-start', marginTop: '8px !important' }}>
                            {delayError}
                        </FormHelperText>
                    )}
                    <Stack spacing={1}>
                        {subActions.map((action) => (
                            <Tooltip
                                key={action.value}
                                title={action.disabled && !delayReady ? 'Set a valid delay first.' : ''}
                            >
                                <FormControlLabel
                                    disabled={action.disabled}
                                    control={<Radio sx={{ display: 'none' }} />}
                                    label={
                                        <span style={labelStyles}>
                                            {action.icon}
                                            {action.label}
                                        </span>
                                    }
                                    sx={{ ...sharedButtonStyles, ...(action.disabled && disabledButtonStyles) }}
                                    value={action.value}
                                    onClick={() => handleDelaySubAction(action.value)}
                                />
                            </Tooltip>
                        ))}
                        <FormControlLabel
                            control={<Radio sx={{ display: 'none' }} />}
                            label={<span style={labelStyles}>← back</span>}
                            sx={sharedButtonStyles}
                            value="back"
                            onClick={() => {
                                setSelectedStep(null);
                                setDelayValue('');
                                setDelayUnit('');
                            }}
                        />
                    </Stack>
                </Stack>
            </FormLayout>
        );
    }

    return (
        <FormLayout guidingText={guidingText} header="choose next step" stepName="actions" icon={<SwapCalls />}>
            <RadioGroup onChange={handleMainStepClick} value={selectedStep}>
                {mainSteps.map((step) => (
                    <Tooltip key={step.value} title={step.disabled ? step.disabledMessage : ''}>
                        <FormControlLabel
                            disabled={step.disabled}
                            control={<Radio sx={{ display: 'none' }} />}
                            label={
                                <span style={labelStyles}>
                                    {step.icon}
                                    {step.label}
                                </span>
                            }
                            sx={{ ...sharedButtonStyles, ...(step.disabled && disabledButtonStyles) }}
                            value={step.value}
                        />
                    </Tooltip>
                ))}
            </RadioGroup>
        </FormLayout>
    );
};

export default CustomCampaignChooseNextStepConditionalSplit;
